// Adapt a chat-proxy session blob (ScheduleRunSession) for replay rendering.
// The chat-proxy now persists Anthropic-style content blocks per turn. We fold
// `tool_result` blocks (delivered as synthetic user-role messages) under their
// matching `tool_use` blocks on the preceding assistant message and produce
// the internal ChatMessage shape the existing UserMessage/AssistantMessage
// renderers already understand.
//
// Background: symlinks/filespin-mcp-server/docs/design/chat-history-blocks-ui-handoff.md.

import type {
  ActionCardData,
  ChatMessage,
  ContentBlock,
  ScheduleRunMessage,
  ScheduleRunSession,
} from './types';

export interface AdaptedScheduleRunMessage {
  message: ChatMessage;
  // Populated only when the source message had no `blocks` (legacy proxy
  // payloads from before the structured-history rollout). Lets the page keep
  // the existing bulleted tool_summaries fallback for stragglers.
  legacyToolSummaries?: string[];
}

function safeJsonParse(raw: string): Record<string, any> | undefined {
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      return parsed as Record<string, any>;
    }
    return { value: parsed };
  } catch {
    return { value: raw };
  }
}

function isOnlyToolResults(blocks: ContentBlock[]): boolean {
  return blocks.length > 0 && blocks.every((b) => b.type === 'tool_result');
}

function buildToolResultIndex(messages: ScheduleRunMessage[]) {
  const index = new Map<
    string,
    { content: string; is_error?: boolean }
  >();
  for (const msg of messages) {
    if (msg.role !== 'user' || !msg.blocks) continue;
    for (const block of msg.blocks) {
      if (block.type === 'tool_result') {
        index.set(block.tool_use_id, {
          content: block.content,
          is_error: block.is_error,
        });
      }
    }
  }
  return index;
}

function buildActionFromToolUse(
  block: Extract<ContentBlock, { type: 'tool_use' }>,
  resultIndex: Map<string, { content: string; is_error?: boolean }>,
): ActionCardData {
  const matched = resultIndex.get(block.id);
  const parsedResult = matched ? safeJsonParse(matched.content) : undefined;

  // Mirrors the live SSE handler in useSSEStream.ts: search_assets exposes
  // query_criteria so the "View" affordance can re-run the search. We don't
  // synthesise a uiAction here (replay is read-only), but populating
  // queryCriteria is harmless and keeps the data shape consistent if it's
  // ever rendered in expanded JSON view.
  const queryCriteria =
    block.name === 'search_assets'
      ? (parsedResult?.query_criteria as Record<string, any> | undefined)
      : undefined;

  let status: ActionCardData['status'];
  if (!matched) {
    // Tool was started but never produced a result in this transcript
    // (truncated turn, error before result, etc.). Show as pending so the
    // input is still inspectable.
    status = 'pending';
  } else if (matched.is_error) {
    status = 'error';
  } else {
    status = 'success';
  }

  return {
    id: `action-${block.id}`,
    toolUseId: block.id,
    tool: block.name,
    input: block.input || {},
    status,
    result: parsedResult,
    queryCriteria,
  };
}

export function adaptScheduleRunMessages(
  session: Pick<ScheduleRunSession, 'id' | 'messages' | 'created_at'>,
): AdaptedScheduleRunMessage[] {
  const messages = session.messages || [];
  const createdAtMs = session.created_at
    ? Date.parse(session.created_at)
    : Date.now();
  const resultIndex = buildToolResultIndex(messages);

  const adapted: AdaptedScheduleRunMessage[] = [];

  messages.forEach((msg, idx) => {
    const id = `${session.id}-${idx}`;

    // Synthetic user message — only `tool_result` blocks. Already folded into
    // the preceding assistant message via the result index; drop it.
    if (
      msg.role === 'user' &&
      msg.blocks &&
      msg.blocks.length > 0 &&
      isOnlyToolResults(msg.blocks)
    ) {
      return;
    }

    if (msg.role === 'user') {
      adapted.push({
        message: {
          id,
          role: 'user',
          content: msg.content || '',
          timestamp: createdAtMs,
        },
        legacyToolSummaries: !msg.blocks ? msg.tool_summaries : undefined,
      });
      return;
    }

    if (!msg.blocks || msg.blocks.length === 0) {
      adapted.push({
        message: {
          id,
          role: 'assistant',
          content: msg.content || '',
          timestamp: createdAtMs,
        },
        legacyToolSummaries: msg.tool_summaries,
      });
      return;
    }

    let textContent = '';
    const actions: ActionCardData[] = [];
    for (const block of msg.blocks) {
      if (block.type === 'text') {
        textContent += block.text;
      } else if (block.type === 'tool_use') {
        actions.push(buildActionFromToolUse(block, resultIndex));
      }
    }

    adapted.push({
      message: {
        id,
        role: 'assistant',
        content: textContent || msg.content || '',
        actions: actions.length > 0 ? actions : undefined,
        timestamp: createdAtMs,
      },
    });
  });

  return adapted;
}
